import _extends from "@swc/helpers/src/_extends.mjs";
import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import React from 'react';
import mergeWith from 'lodash/mergeWith';
import classnames from 'classnames';
import { RichContentViewer } from 'wix-rich-content-viewer';
import { Url } from '@wix/da-url';
import isEmpty from 'lodash/isEmpty';
import { IMAGE_TYPE, IMAGE_TYPE_LEGACY } from '../../plugins/plugin-image/types';
import { typeMapper as emoteTypeMapper } from '../../plugins/emote/typeMapper';
import { typeMapper as mentionTypeMapper } from '../../plugins/mention/typeMapper';
import { typeMapper as galleryTypeMapper } from '../../plugins/plugin-gallery/typeMapper';
import { typeMapper as imageTypeMapper } from '../../plugins/plugin-image/typeMapper';
import { typeMapper as videoTypeMapper } from '../../plugins/plugin-video/typeMapper';
import { linkTypeMapper, LINK_TYPE } from 'wix-rich-content-plugin-link/viewer';
import { giphyTypeMapper } from 'wix-rich-content-plugin-giphy/viewer';
import { typeMapper as linkPreviewTypeMapper } from '../../plugins/plugin-link-preview/typeMapper';
import { dividerTypeMapper } from 'wix-rich-content-plugin-divider/viewer';
import { HashtagDecorator } from 'wix-rich-content-plugin-hashtag/viewer';
import { textColorInlineStyleMapper, textHighlightInlineStyleMapper, TEXT_COLOR_TYPE, TEXT_HIGHLIGHT_TYPE } from 'wix-rich-content-plugin-text-color/viewer';
import withFeatureFlags from '@wix/da-react-context/pkg/flags/withFeatureFlags';
import baseTheme from '../../theme';
import { ThemeContext } from '@wix/da-react-context/pkg/ThemeContext';
import LazyContext from '../../../LazyLoadingImage/LazyContext';
import ddt from '@wix/da-ddt';
import daContentNormalizer from './daContentNormalizer';
import { viewerCustomForegroundStyleFn, styleSelectionPredicate, viewerCustomBackgroundStyleFn } from '../../plugins/text-color-style-fn';
import { scrollIntoViewWithHeader } from '../../../utils/scrollIntoViewWithHeader';
const anchorTarget = '_top';
const relValue = 'noreferrer';
export class DaViewer extends React.Component {
    shouldComponentUpdate(nextProps) {
        if (nextProps.globalTheme !== this.props.globalTheme) {
            return true;
        }
        return false;
    }
    initViewerProps(props) {
        const { imageComponent , galleryComponent , videoComponent  } = props;
        this.theme = mergeWith(_object_spread({}, baseTheme), props.theme || {}, (defaultStyleClassName, themeClassName)=>classnames(defaultStyleClassName, themeClassName));
        this.typeMappers = [
            imageTypeMapper(imageComponent),
            mentionTypeMapper,
            emoteTypeMapper,
            linkTypeMapper,
            linkPreviewTypeMapper,
            galleryTypeMapper(galleryComponent),
            videoTypeMapper(videoComponent),
            giphyTypeMapper,
            dividerTypeMapper
        ];
        this.decorators = [
            new HashtagDecorator({
                theme: this.theme,
                createHref: (tag)=>Url.tagsPageLink(tag)
            })
        ];
    }
    getThemeClassName() {
        const { isDarkTheme  } = this.props;
        return `da-editor-theme-${isDarkTheme ? 'dark' : 'bright'}`;
    }
    getWrapperProps() {
        const { fixedParagraph , showMediaAlwaysSmall  } = this.props;
        const wrapperProps = {};
        if (fixedParagraph) {
            wrapperProps['data-daeditor-fixed-paragraph'] = true;
        }
        if (showMediaAlwaysSmall) {
            wrapperProps['data-daeditor-small-media'] = true;
        }
        wrapperProps.className = this.getThemeClassName();
        return wrapperProps;
    }
    render() {
        const { isMobile , content , fixedParagraph , showMediaAlwaysSmall , disableLazyLoading  } = this.props;
        if (isEmpty(content)) {
            return null;
        }
        const settings = {
            fixedParagraph,
            showMediaAlwaysSmall
        };
        // apply some global settings to relevant plugins
        const config = {
            [IMAGE_TYPE]: settings,
            [IMAGE_TYPE_LEGACY]: settings,
            [LINK_TYPE]: {}
        };
        const initState = daContentNormalizer(content);
        return content ? /*#__PURE__*/ React.createElement("div", _extends({}, this.getWrapperProps()), /*#__PURE__*/ React.createElement(LazyContext.Provider, {
            value: !disableLazyLoading
        }, /*#__PURE__*/ React.createElement(RichContentViewer, {
            typeMappers: this.typeMappers,
            decorators: this.decorators,
            initialState: initState,
            inlineStyleMappers: this.getInlineStyleMappers(initState),
            theme: this.theme,
            isMobile: isMobile,
            anchorTarget: anchorTarget,
            relValue: relValue,
            config: config,
            customAnchorScroll: this.customAnchorScroll,
            onError: (e)=>{
                ddt.error('editor', 'Failed to render editor view', e);
            }
        }))) : null;
    }
    constructor(props){
        super(props);
        this.getInlineStyleMappers = (raw)=>[
                textColorInlineStyleMapper({
                    [TEXT_COLOR_TYPE]: {
                        styleSelectionPredicate,
                        customStyleFn: viewerCustomForegroundStyleFn
                    }
                }, raw),
                textHighlightInlineStyleMapper({
                    [TEXT_HIGHLIGHT_TYPE]: {
                        styleSelectionPredicate,
                        customStyleFn: viewerCustomBackgroundStyleFn
                    }
                }, raw)
            ];
        this.customAnchorScroll = (ev, anchor)=>{
            const anchorEl = document.getElementById(`viewer-${anchor}`);
            anchorEl && scrollIntoViewWithHeader(anchorEl, this.props.isMobile, 0, undefined, true, true);
        };
        this.initViewerProps(props);
    }
}
const globalThemeHoc = (Component)=>(props)=>{
        return /*#__PURE__*/ React.createElement(ThemeContext.Consumer, null, (theme)=>/*#__PURE__*/ React.createElement(Component, _extends({
                globalTheme: theme
            }, props)));
    };
export default globalThemeHoc(withFeatureFlags(DaViewer));
